/* exported Infinator */
const ACTIVATION_DISTANCE_FROM_BOTTOM_OF_GRID_CONTAINER = 1500; // In px units
const FIRST_PAGE_DEFAULT = 1;
const MINIMUM_SCROLL_DISTANCE = 200; // In px units
const SCROLL_EVENT_DELAY = 2000; // Milliseconds
const LOADING_ELEMENT_ID = 'infinatorLoader';
const SCROLL_TO_TOP_BTN_ID = 'infinatorSTP';
const PREVIOUS_PAGE_BTN_ELEMENT_ID = 'infinatorPreviousPage';
const NEXT_PAGE_BTN_ELEMENT_ID = 'infinatorNextPage';
const DIRECTIONS = {
	PREVIOUS: 'prev',
	NEXT: 'next',
};
const TYPES = {
	SCROLL: 'scroll',
	CLICK: 'click',
};
class Infinator {
	constructor(settings) {
		// The page stated in the url, defaults to 1
		this.currentPage = new URL(window.location.href).searchParams.get('page') ?? FIRST_PAGE_DEFAULT;
		this.previousPage = this.currentPage;
		// Define class settings
		this.settings = {
			type: 'scroll', // Valid values are: 'scroll' or 'click' - The event listener type to load the next page
			productsGridSelector: '.collection-grid', // The container that the new products will be appended (Make sure it contains ONLY product blocks).
			productsGridSectionId: 'main-collection', // The section id that contains the products grid (only this section will be fetched)
			loadingText: 'Loading...', // The loading element text - Mostly should be passed in from the translations
			loadingBtnClassName: '', // Space seperated classes to be added to the loading element
			previousPageButtonText: 'Load Previous Page',
			nextPageButtonText: 'Load next page',
			loadPageButtonClassName: '',
			scrollToTopBtnText: 'Top', // Scroll to top button inner text
			scrollToTopBtnClassName: '', // Scroll to top button classes
			filtersChangeEvent: 'filter:changed', // The custom event dispatched from the 'theme.js' file when a filter is changed,
			paginationSelector: false, // The default pagination element to remove (pass false to leave the pagination element)
			activationOffsetBottom: ACTIVATION_DISTANCE_FROM_BOTTOM_OF_GRID_CONTAINER, // This distance in px from the bottom of the products container that activates next page render
			beforeEach: function () {
				/* Default empty funciton */
			}, // A function that will be invoked before every page render
			afterEach: function () {
				/* Default empty funciton */
			}, // A callback function that will be invoked after the products has been added to the DOM
			afterFilter: function () { /* Default empty funciton */ }, // A callback function that will be invoked after each filtersChangeEvent
			...settings, // Overides all the default settings
		};
		// The distance from the top of the page to the top of the products grid container in units of px
		// This distance will be reducted in the scroll calculation to eliminate header and unrelated layout effects.
		this.containerOffsetHeight = document
			.querySelector(this.settings.productsGridSelector)
			.getBoundingClientRect().top;
		// This let us know the height of a typical container for previous page loading scroll
		this.baseContainerHeight = document.querySelector(
			this.settings.productsGridSelector
		).offsetHeight;
		// Defaults to the current url
		this.requestedPageUrl = window.location.href;
		// Bind all methods
		this.bindAllMethods.call(this);
		// Re-initializes the class on filter change (restart from scratch to avoid bugs)
		document.addEventListener(this.settings.filtersChangeEvent, () => {
			this.reinitialize();
			this.settings.afterFilter();
		});
		// Initialize the infinite scroll
		this.init();
	}

	bindAllMethods() {
		this.init = this.init.bind(this);
		this.scrollUserToLastViewdProduct = this.scrollUserToLastViewdProduct.bind(this);
		this.setPaginationEventListener = this.setPaginationEventListener.bind(this);
		this.handlePaginationRemoval = this.handlePaginationRemoval.bind(this);
		this.updateContainerHeight = this.updateContainerHeight.bind(this);
		this.createScrollToTopBtn = this.createScrollToTopBtn.bind(this);
		this.showScrollToTopBtn = this.showScrollToTopBtn.bind(this);
		this.hideScrollToTopBtn = this.hideScrollToTopBtn.bind(this);
		this.createLoadNextPageButton = this.createLoadNextPageButton.bind(this);
		this.setReversePagination = this.setReversePagination.bind(this);
		this.prepareProductsTrackingData = this.prepareProductsTrackingData.bind(this);
		this.setProductEventListener = this.setProductEventListener.bind(this);
		this.setInitialHistory = this.setInitialHistory.bind(this);
		this.createLoadingElement = this.createLoadingElement.bind(this);
		this.handleUserScroll = this.handleUserScroll.bind(this);
		this.initiatePageLoadSequence = this.initiatePageLoadSequence.bind(this);
		this.isScrolledToThreshold = this.isScrolledToThreshold.bind(this);
		this.setLoadingState = this.setLoadingState.bind(this);
		this.updateUrl = this.updateUrl.bind(this);
		this.renderRequestedPage = this.renderRequestedPage.bind(this);
		this.addNewProductsToDOM = this.addNewProductsToDOM.bind(this);
		this.getRequestedPageHTML = this.getRequestedPageHTML.bind(this);
		this.getNewUrlQueryParams = this.getNewUrlQueryParams.bind(this);
		this.destroy = this.destroy.bind(this);
		this.reinitialize = this.reinitialize.bind(this);
		this.isIos = this.isIos.bind(this);
	}

	init() {
		this.scrollUserToLastViewdProduct();
		this.createLoadingElement();
		this.setPaginationEventListener();
		this.handlePaginationRemoval();
		this.updateContainerHeight();
		this.setReversePagination();
		this.prepareProductsTrackingData();
		this.setInitialHistory();
		this.createScrollToTopBtn();
	}

	scrollUserToLastViewdProduct() {
		if (window.sessionStorage.getItem(window.location.pathname + '-product')) {
			document
				.querySelector(
					`[data-infinator-id="${window.sessionStorage.getItem(
						window.location.pathname + '-product'
					)}"]`
				)
				?.scrollIntoView({
					block: 'center',
				});
			window.sessionStorage.removeItem(window.location.pathname + '-product');
			window.sessionStorage.removeItem(window.location.pathname + '-page');
		}
		if (!this.isIos()) {
			history.scrollRestoration = 'manual';
		}
	}

	prepareProductsTrackingData() {
		document.querySelectorAll('[data-infinator-id]:not([tracked])').forEach((product) => {
			product.setAttribute('tracked', '');
			product.setAttribute('data-infinator-page', this.currentPage);
			this.setProductEventListener(product);
		});
	}

	setProductEventListener(product) {
		product.addEventListener('click', (e) => {
			window.sessionStorage.setItem(
				window.location.pathname + '-product',
				e.target.closest('[data-infinator-id]').dataset.infinatorId
			);
			window.sessionStorage.setItem(
				window.location.pathname + '-page',
				e.target.closest('[data-infinator-page]').dataset.infinatorPage
			);
		});
	}

	setInitialHistory() {
		history.replaceState(
			{ page: this.currentPage },
			null,
			this.getNewUrlQueryParams(window.location.href, { page: this.currentPage })
		);
	}

	createScrollToTopBtn() {
		if (!this.settings.scrollToTopBtnText) {
			return;
		}
		const scrollToTopBtn = document.createElement('button');
		scrollToTopBtn.innerText = this.settings.scrollToTopBtnText;
		scrollToTopBtn.id = SCROLL_TO_TOP_BTN_ID;
		scrollToTopBtn.style.cssText =
			'display: flex;align-items: center;justify-content: center;transition: transform .6s;position: fixed; right:5px; bottom: 280px; background: #000; color: white; z-index: 10;padding: 0.7rem 4px; font-size: 11px ;font-weight: 500;border-radius: 10rem; transform: translateX(5rem) rotate(360deg);';
		if (this.settings.scrollToTopBtnClassName) {
			// Add the custom classes passed in the settings object
			for (const className of this.settings.scrollToTopBtnClassName.split(' ')) {
				scrollToTopBtn.classList.add(className);
			}
		}

		const scrollEvent = () => {
			if (window.scrollY < MINIMUM_SCROLL_DISTANCE) {
				this.hideScrollToTopBtn();
				return;
			}
			this.showScrollToTopBtn();
		};
		window.addEventListener('scroll', scrollEvent);

		const clickEvent = () => {
			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
			this.hideScrollToTopBtn();
		};
		scrollToTopBtn.addEventListener('click', clickEvent);

		// Appends after the container (not inside) so it will always be at the bottom
		document.body.append(scrollToTopBtn);
	}

	showScrollToTopBtn() {
		document.getElementById(SCROLL_TO_TOP_BTN_ID).style.transform = 'unset';
	}

	hideScrollToTopBtn() {
		document.getElementById(SCROLL_TO_TOP_BTN_ID).style.transform =
			'translateX(5rem) rotate(360deg)';
	}
	setPaginationEventListener() {
		if (this.settings.type.toLocaleLowerCase() === TYPES.CLICK) {
			this.createLoadNextPageButton();
		} else {
			// Default and fallback behaviour is scroll
			window.addEventListener(TYPES.SCROLL, this.handleUserScroll);
		}
	}

	handlePaginationRemoval() {
		document.querySelector(this.settings.paginationSelector)?.remove();
	}

	// Once the products have been loaded, the height of the container is changed. Update the new height in the class state.
	updateContainerHeight() {
		this.conatinerHeight = document.querySelector(this.settings.productsGridSelector).offsetHeight;
	}

	createLoadNextPageButton() {
		const nextPageBtn = document.createElement('button');
		nextPageBtn.innerText = this.settings.nextPageButtonText;
		nextPageBtn.id = NEXT_PAGE_BTN_ELEMENT_ID;
		nextPageBtn.classList.add('infinator-btn');
		if (this.settings.loadPageButtonClassName) {
			// Add the custom classes passed in the settings object
			for (const className of this.settings.loadPageButtonClassName.split(' ')) {
				nextPageBtn.classList.add(className);
			}
		}
		nextPageBtn.addEventListener(TYPES.CLICK, () => this.initiatePageLoadSequence(DIRECTIONS.NEXT));
		document.querySelector(this.settings.productsGridSelector).after(nextPageBtn);
	}

	setReversePagination() {
		if (Number(this.currentPage) <= FIRST_PAGE_DEFAULT) {
			return;
		}
		const previousPageBtn = document.createElement('button');
		previousPageBtn.innerText = this.settings.previousPageButtonText;
		previousPageBtn.id = PREVIOUS_PAGE_BTN_ELEMENT_ID;
		previousPageBtn.style.cssText = 'margin-bottom: 10px;';
		previousPageBtn.classList.add('infinator-btn');
		if (this.settings.loadPageButtonClassName) {
			// Add the custom classes passed in the settings object
			for (const className of this.settings.loadPageButtonClassName.split(' ')) {
				previousPageBtn.classList.add(className);
			}
		}
		previousPageBtn.addEventListener('click', () =>
			this.initiatePageLoadSequence(DIRECTIONS.PREVIOUS)
		);
		document.querySelector(this.settings.productsGridSelector).before(previousPageBtn);
	}

	createLoadingElement() {
		const loadingEl = document.createElement('span');
		loadingEl.innerText = this.settings.loadingText;
		loadingEl.id = LOADING_ELEMENT_ID;
		loadingEl.style.textAlign = 'center';
		loadingEl.style.display = 'none'; // Starts invisible, will be shown on next page load
		if (this.settings.loadingBtnClassName) {
			// Add the custom classes passed in the settings object
			for (const className of this.settings.loadingBtnClassName.split(' ')) {
				loadingEl.classList.add(className);
			}
		}
		// Appends after the container (not inside) so it will always be at the bottom
		document.querySelector(this.settings.productsGridSelector).after(loadingEl);
	}

	handleUserScroll() {
		try {
			// Check if the user is scrolled enough to trigger next page load and prevent next page load if previous is still loading
			if (!this.isScrolledToThreshold() || this.isLoading) {
				return false;
			}
			this.initiatePageLoadSequence(DIRECTIONS.NEXT);
		} catch (e) {
			console.warn(e);
		}
	}

	async initiatePageLoadSequence(direction) {
		this.setLoadingState(true);
		this.settings.beforeEach();
		this.updateUrl(direction);
		const isNextPageReloaded = await this.renderRequestedPage(direction);
		if (isNextPageReloaded) {
			this.updateContainerHeight();
			this.settings.afterEach();
		}
		this.setLoadingState(false);
	}

	// Check if the user is scrolled at least 'ACTIVATION_DISTANCE_FROM_BOTTOM_OF_GRID_CONTAINER'px from the bottom of the products container
	isScrolledToThreshold() {
		// Subtract the containerOffsetHeight to eliminate header and external layout impact.
		const ContainerBottomAbsolutePosition = this.conatinerHeight - this.containerOffsetHeight;
		const scrollDistanceFromContainerBottom = ContainerBottomAbsolutePosition - window.scrollY;
		return scrollDistanceFromContainerBottom < this.settings.activationOffsetBottom;
	}

	setLoadingState(isLoading) {
		this.isLoading = isLoading; // Important because the next scroll happens before the products loaded so the handle function will run too many times
		// Show or hide the loding element based on the loading state
		const loadingElement = document.getElementById(LOADING_ELEMENT_ID);
		if (!loadingElement) {
			// Prevent error if the loading element is not in the DOM for some reason, it's not important enough to terminate the operation.
			return;
		}
		return (loadingElement.style.display = isLoading ? 'block' : 'none');
	}

	// Keep track of the page state in the URL
	updateUrl(direction) {
		// Make sure number addition rather than string concatination
		let requestedPageNumber;
		if (direction === DIRECTIONS.NEXT) {
			// eslint-disable-next-line no-magic-numbers
			this.currentPage = Number(this.currentPage) + 1;
			requestedPageNumber = this.currentPage;
		} else {
			// eslint-disable-next-line no-magic-numbers
			this.previousPage = Number(this.previousPage) - 1;
			requestedPageNumber = this.previousPage;
		}
		this.requestedPageUrl = this.getNewUrlQueryParams(window.location.href, {
			page: requestedPageNumber,
		});
		// Use replaceState so if the users clicks back he wouldn't have to go through all the previously loaded pages
		history.replaceState({ page: requestedPageNumber }, null, this.requestedPageUrl);
	}

	async renderRequestedPage(direction) {
		// Get the URL with all the query params of the requested page to fetch and display it.
		const endpoint = this.getNewUrlQueryParams(this.requestedPageUrl, {
			// eslint-disable-next-line camelcase
			section_id: this.settings.productsGridSectionId,
		});
		const requestedPageDOM = await this.getRequestedPageHTML(endpoint);
		const newProducts = requestedPageDOM.querySelector(this.settings.productsGridSelector);
		if (!(newProducts && newProducts?.children?.length)) {
			// This is the last page, no need for the infinite scroll functionallity anymore
			// eslint-disable-next-line no-magic-numbers
			return this.destroy(this.currentPage - 1);
		}
		if (Number(this.previousPage) === FIRST_PAGE_DEFAULT) {
			// No need for previous page button
			document.getElementById(PREVIOUS_PAGE_BTN_ELEMENT_ID)?.remove();
		}
		// Make sure to add the children of the products container to the container, not the container itself (otherwise you'll get a container withing a container)
		this.addNewProductsToDOM(newProducts, direction);
		return true;
	}

	addNewProductsToDOM(newProducts, direction) {
		const productsContainer = document.querySelector(this.settings.productsGridSelector);

		if (direction === DIRECTIONS.NEXT) {
			for (let i = 0; i < newProducts.children.length; i++) {
				const newProduct = newProducts.children[i].cloneNode(true);
				newProduct.dataset.infinatorPage = this.currentPage;
				productsContainer.appendChild(newProduct);
				this.setProductEventListener(newProduct);
			}
		} else {
			// Reverse order
			// eslint-disable-next-line no-magic-numbers
			for (let i = newProducts.children.length - 1; i >= 0; i--) {
				const newProduct = newProducts.children[i].cloneNode(true);
				newProduct.dataset.infinatorPage = this.previousPage;
				productsContainer.prepend(newProduct);
				this.setProductEventListener(newProduct);
			}
			window.scrollTo({
				top: this.baseContainerHeight + window.scrollY,
			});
		}
	}

	async getRequestedPageHTML(requestedPageUrl) {
		const response = await fetch(requestedPageUrl);
		const requestedPageHTMLString = await response.text();
		return new DOMParser().parseFromString(requestedPageHTMLString, 'text/html');
	}

	// All of the pagination, filters and sort logic happens in the URL. Keep it clean and updated.
	getNewUrlQueryParams(previousUrl, params) {
		const url = new URL(previousUrl);
		const urlSearchParams = url.searchParams;
		for (const [key, value] of Object.entries(params)) {
			urlSearchParams.set(key, value); // Use set to override the param if it's already set
		}
		if (Number(urlSearchParams.get('page')) === FIRST_PAGE_DEFAULT) {
			urlSearchParams.delete('page');
		}
		return url.toString();
	}

	// Remove everything the Infinator has added to the DOM
	destroy(page = null) {
		window.removeEventListener('scroll', this.handleUserScroll);
		// Reset current page and the URL
		this.currentPage = page ?? FIRST_PAGE_DEFAULT;
		this.setInitialHistory();
		// Remove the added loading elements if still in the DOM
		document.getElementById(LOADING_ELEMENT_ID)?.remove();
		document.getElementById(NEXT_PAGE_BTN_ELEMENT_ID)?.remove();
		document.getElementById(PREVIOUS_PAGE_BTN_ELEMENT_ID)?.remove();
	}

	reinitialize() {
		this.destroy();
		this.init();
	}

	isIos() {
		return (
			['iPad Simulator', 'iPhone Simulator', 'iPad', 'iPhone'].includes(navigator.platform) ||
			// IPad on iOS 13 detection
			(navigator.userAgent.includes('Mac') && 'ontouchend' in document)
		);
	}
}

window.Infinator = {};

window.Infinator.accessibility = function() {
	window.Infinator.instance.destroy(window.Infinator.instance.currentPage);
	const newConfig = {...window.Infinator.config, type: TYPES.CLICK};
	const newInfinator = new Infinator(newConfig);
	window.Infinator.instance = newInfinator;
};

/* eslint-disable capitalized-comments */

// Uninified theme.liquid script
// (function () {
// if (!window.sessionStorage.getItem(window.location.pathname + '-page')) {
// 		// This is the first visit, do nothing.
// 		return;
// 	}
// 	// Not the first time we are in this collection page
// 	if (window.sessionStorage.getItem('infinator_reloaded')) {
// 		// if we already reloaded this page
// 		window.sessionStorage.removeItem('infinator_reloaded');
// 		return true;
// 	}
// 	// We haven't reloaded this page yet
// 	window.sessionStorage.setItem('infinator_reloaded', 1);
// 	const url = new URL(window.location.href);
// 	url.searchParams.set('page', window.sessionStorage.getItem(window.location.pathname + '-page'));
// 	window.location.href = url.toString();
// })();
